import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaUser,
  FaPlus,
  FaEdit,
  FaSave,
  FaTimes,
  FaSearch,
  FaFilter,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaStore,
  FaExclamationTriangle,
  FaInfoCircle
} from "react-icons/fa";

function ClientsManager() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterWholesale, setFilterWholesale] = useState("all");

  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    client_address: "",
    customer_province: "",
    customer_city: "",
    client_cellphone: "",
    is_wholesale: false,
    observations: ""
  });

  const [errors, setErrors] = useState({});
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingClient, setPermanentDeletingClient] = useState(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await getData("/api/admin/get_clients");

      if (response?.data?.clients) {
        setClients(response.data.clients);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error("Error cargando clientes:", error);
      toastError("Error al cargar los clientes");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.client_id.trim()) {
      newErrors.client_id = "El DNI/CUIT es requerido";
    } else if (!/^\d{8,11}$/.test(formData.client_id.replace(/\D/g, ''))) {
      newErrors.client_id = "El DNI/CUIT debe tener entre 8 y 11 dígitos";
    }

    if (!formData.client_name.trim()) {
      newErrors.client_name = "El nombre del cliente es requerido";
    }

    if (!formData.client_address.trim()) {
      newErrors.client_address = "La dirección es requerida";
    }

    if (!formData.client_cellphone.trim()) {
      newErrors.client_cellphone = "El teléfono es requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Formato según documentación API
      const clientData = {
        data: {
          ...formData,
          user: 1 // TODO: Obtener del contexto de usuario
        }
      };

      if (editingClient) {
        clientData.data.id = editingClient.id;
        const response = await putData("/api/admin/update_client", clientData);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        // Actualización optimista
        setClients(prev => prev.map(client => 
          client.id === editingClient.id ? { ...client, ...formData } : client
        ));
        
        toastSuccess("Cliente actualizado exitosamente");
      } else {
        const response = await postData("/api/admin/create_client", clientData);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        // Actualización optimista al crear
        if (response.data?.client) {
          const newClient = {
            id: response.data.client.id || response.data.id,
            ...formData
          };
          setClients(prev => [newClient, ...prev]);
        }
        
        toastSuccess("Cliente creado exitosamente");
        loadClients().catch(() => {});
      }

      resetForm();
    } catch (error) {
      console.error("Error guardando cliente:", error);
      if (error.response?.status === 400) {
        toastError("Error: Posiblemente el DNI/CUIT ya existe");
      } else {
        toastError("Error al guardar el cliente");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_id: client.client_id || "",
      client_name: client.client_name || "",
      client_address: client.client_address || "",
      customer_province: client.customer_province || "",
      customer_city: client.customer_city || "",
      client_cellphone: client.client_cellphone || "",
      is_wholesale: Boolean(client.is_wholesale), // Convertir explícitamente a boolean
      observations: client.observations || ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      client_name: "",
      client_address: "",
      customer_province: "",
      customer_city: "",
      client_cellphone: "",
      is_wholesale: false,
      observations: ""
    });
    setEditingClient(null);
    setShowForm(false);
    setErrors({});
  };

  // Funciones de eliminación PERMANENTE
  const handlePermanentDelete = (client) => {
    setPermanentDeletingClient(client);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingClient) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingClient.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_client', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setClients(prevClients => prevClients.filter(client => client.id !== permanentDeletingClient.id));
      
      toastSuccess(`Cliente "${permanentDeletingClient.client_name}" eliminado permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingClient(null);
    } catch (error) {
      console.error('Error eliminando cliente:', error);
      toastError(error.message || 'Error al eliminar el cliente definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingClient(null);
  };

  // Optimizar filtrado con useMemo
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = 
        client.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.client_id?.includes(searchTerm) ||
        client.client_address?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesWholesale = 
        filterWholesale === "all" ||
        (filterWholesale === "wholesale" && client.is_wholesale) ||
        (filterWholesale === "retail" && !client.is_wholesale);

      return matchesSearch && matchesWholesale;
    });
  }, [clients, searchTerm, filterWholesale]);

  const formatPhone = (phone) => {
    if (!phone) return "";
    // Formatear teléfono argentino
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatClientId = (clientId) => {
    if (!clientId) return "";
    const cleaned = clientId.replace(/\D/g, '');
    if (cleaned.length === 8) {
      // DNI
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
    } else if (cleaned.length === 11) {
      // CUIT
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
    }
    return clientId;
  };

  if (showForm) {
    return (
      <div className="clients-manager">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <FaUser className="me-2 text-primary" />
                {editingClient ? "Editar Cliente" : "Nuevo Cliente"}
              </h5>
              <button
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FaTimes className="me-1" />
                Cancelar
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    <FaIdCard className="me-1" />
                    DNI/CUIT *
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.client_id ? "is-invalid" : ""}`}
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    placeholder="Ej: 12345678 o 20123456789"
                    required
                  />
                  {errors.client_id && (
                    <div className="invalid-feedback">{errors.client_id}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    <FaUser className="me-1" />
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.client_name ? "is-invalid" : ""}`}
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    placeholder="Nombre y apellido del cliente"
                    required
                  />
                  {errors.client_name && (
                    <div className="invalid-feedback">{errors.client_name}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-8 mb-3">
                  <label className="form-label">
                    <FaMapMarkerAlt className="me-1" />
                    Dirección *
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.client_address ? "is-invalid" : ""}`}
                    name="client_address"
                    value={formData.client_address}
                    onChange={handleInputChange}
                    placeholder="Dirección completa"
                    required
                  />
                  {errors.client_address && (
                    <div className="invalid-feedback">{errors.client_address}</div>
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    <FaPhone className="me-1" />
                    Teléfono *
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.client_cellphone ? "is-invalid" : ""}`}
                    name="client_cellphone"
                    value={formData.client_cellphone}
                    onChange={handleInputChange}
                    placeholder="Ej: +54 9 11 1234-5678"
                    required
                  />
                  {errors.client_cellphone && (
                    <div className="invalid-feedback">{errors.client_cellphone}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    <FaMapMarkerAlt className="me-1" />
                    Provincia
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="customer_province"
                    value={formData.customer_province}
                    onChange={handleInputChange}
                    placeholder="Ej: Buenos Aires, Córdoba, etc."
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    <FaMapMarkerAlt className="me-1" />
                    Ciudad
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="customer_city"
                    value={formData.customer_city}
                    onChange={handleInputChange}
                    placeholder="Ej: Capital Federal, Rosario, etc."
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="is_wholesale"
                      id="is_wholesale"
                      checked={formData.is_wholesale}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="is_wholesale">
                      <FaStore className="me-1" />
                      Cliente Mayorista
                    </label>
                  </div>
                  <div className="form-text">
                    Marcar si el cliente compra al por mayor
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  <FaInfoCircle className="me-1" />
                  Observaciones
                </label>
                <textarea
                  className="form-control"
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Notas adicionales sobre el cliente..."
                />
              </div>
            </div>

            <div className="card-footer">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="spinner-border spinner-border-sm me-2"></div>
                  ) : (
                    <FaSave className="me-1" />
                  )}
                  {editingClient ? "Actualizar" : "Crear"} Cliente
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-manager">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <FaUser className="me-2 text-primary" />
              Gestión de Clientes
            </h5>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FaPlus className="me-1" />
              Nuevo Cliente
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Filtros y búsqueda */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre, DNI/CUIT o dirección..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={filterWholesale}
                onChange={(e) => setFilterWholesale(e.target.value)}
              >
                <option value="all">Todos los clientes</option>
                <option value="retail">Solo Minoristas</option>
                <option value="wholesale">Solo Mayoristas</option>
              </select>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={loadClients}
                disabled={loading}
              >
                <FaFilter />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : filteredClients.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>DNI/CUIT</th>
                    <th>Nombre</th>
                    <th>Dirección</th>
                    <th>Ubicación</th>
                    <th>Teléfono</th>
                    <th>Tipo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>{client.id}</td>
                      <td>
                        <code>{formatClientId(client.client_id) || 'NULL'}</code>
                      </td>
                      <td>
                        <strong>{client.client_name || 'NULL'}</strong>
                      </td>
                      <td>
                        <small>{client.client_address || 'NULL'}</small>
                      </td>
                      <td>
                        <small>
                          {client.customer_city && client.customer_province 
                            ? `${client.customer_city}, ${client.customer_province}`
                            : client.customer_province || client.customer_city || '-'}
                        </small>
                      </td>
                      <td>
                        <small>{formatPhone(client.client_cellphone) || 'NULL'}</small>
                      </td>
                      <td>
                        {client.is_wholesale ? (
                          <span className="badge bg-warning text-dark">
                            <FaStore className="me-1" />
                            Mayorista
                          </span>
                        ) : (
                          <span className="badge bg-info">
                            <FaUser className="me-1" />
                            Minorista
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(client)}
                            title="Editar cliente"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handlePermanentDelete(client)}
                            title="Eliminar definitivamente (irreversible)"
                          >
                            <FaExclamationTriangle />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">
              <FaInfoCircle className="me-2" />
              {searchTerm || filterWholesale !== "all"
                ? "No se encontraron clientes con los filtros aplicados"
                : "No hay clientes registrados"}
            </div>
          )}
        </div>

        <div className="card-footer">
          <small className="text-muted">
            Total de clientes: {clients.length} | Mostrando: {filteredClients.length}
          </small>
        </div>
      </div>

      {/* Modal de confirmación de eliminación PERMANENTE */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente cliente?</p>
                {permanentDeletingClient && (
                  <div className="alert alert-warning">
                    <strong>Cliente:</strong> {permanentDeletingClient.client_name}<br/>
                    <strong>DNI/CUIT:</strong> {permanentDeletingClient.client_id}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El cliente se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                  <li>Se perderá todo el historial asociado a este cliente</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este cliente,
                    considere usar una marca de inactivo en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsManager;